export interface ArtistXP {
  artistId: string;
  xp: number;
  level: number;
  levelName: string;
  nextLevelXP: number;
  totalEarned: number;
  questsCompleted: number;
}

export interface Artist {
  id: string;
  name: string;
  tagline: string;
  bio: string;
  avatar: string;
  coverImage: string;
  stats: {
    followers: string;
    ipCollections: number;
    completedQuests: number;
  };
  verified: boolean;
  genre: string[];
  ipImages: string[];
  accentColor: string;
  secondaryColor: string;
  groupName: string;
  debutYear: number;
  category: "kpop" | "drama" | "film" | "webtoon";
  categoryLabel: string;
}

export interface QuestPresets {
  /** 권장 AI 도구 ID */
  tool?: string;
  /** 권장 샷 사이즈 ID */
  shotSize?: string;
  /** 권장 카메라 앵글 ID */
  cameraAngle?: string;
  /** 권장 카메라 무브먼트 ID */
  cameraMove?: string;
  /** 권장 렌즈 타입 ID */
  lens?: string;
  /** 권장 조명 ID */
  lighting?: string;
  /** 권장 시간대 ID */
  timeOfDay?: string;
  /** 권장 날씨 ID */
  weather?: string;
  /** 권장 영상 길이 */
  duration?: string;
}

export interface Quest {
  id: string;
  artistId: string;
  title: string;
  description: string;
  difficulty: "Easy" | "Medium" | "Hard";
  difficultyStars: number;
  reward: number;
  rewardType: string;
  thumbnail: string;
  progress: number;
  participants: number;
  participantAvatars: string[];
  deadline: string;
  tag: string;
  color: "cyan" | "magenta" | "yellow" | "green" | "orange" | "purple";
  questType: "mv" | "dance" | "scene" | "poster" | "trailer" | "challenge";
  /** 퀘스트 발행자가 지정한 권장 프리셋 */
  presets?: QuestPresets;
}

export interface Reward {
  id: string;
  artistId: string;
  title: string;
  description: string;
  category: "offline" | "digital" | "credit" | "exclusive";
  categoryLabel: string;
  xpCost: number;
  stock: number;
  totalStock: number;
  icon: string;
  color: string;
  badge?: string;
  /** @deprecated use endDate instead */
  deadline?: string;
  /** ISO date string: YYYY-MM-DD — 제공 시작일 */
  startDate: string;
  /** ISO date string: YYYY-MM-DD — 제공 종료일 */
  endDate: string;
  image: string;
  perks: string[];
  isLimited: boolean;
}

export const artists: Artist[] = [
  {
    id: "artist-001",
    name: "SEOL",
    groupName: "AURORA",
    debutYear: 2021,
    category: "kpop",
    categoryLabel: "K-POP 아이돌",
    tagline: "K-Pop Idol & Visual Artist",
    bio: "AURORA의 메인 보컬 SEOL. 청량한 목소리와 감성적인 퍼포먼스로 팬들의 마음을 사로잡는 아이돌. 팬들과 함께 뮤직비디오 신을 만들어가요.",
    avatar: "https://public.readdy.ai/ai/img_res/1b16e0d70170eb2cf150180194fb5c34.jpg",
    coverImage: "https://readdy.ai/api/search-image?query=kpop%20idol%20stage%20performance%2C%20pink%20and%20white%20pastel%20lighting%2C%20cherry%20blossom%20petals%20falling%2C%20dreamy%20atmosphere%2C%20wide%20panoramic%20concert%20stage%2C%20elegant%20feminine%20aesthetic&width=1400&height=600&seq=kpop-cover-001&orientation=landscape",
    stats: { followers: "3.2M", ipCollections: 28, completedQuests: 4821 },
    verified: true,
    genre: ["뮤직비디오 신", "안무", "퍼포먼스"],
    accentColor: "#ff6eb4",
    secondaryColor: "#ffd6e8",
    ipImages: [
      "https://readdy.ai/api/search-image?query=kpop%20idol%20character%20in%20white%20dress%2C%20cherry%20blossom%20background%2C%20elegant%20pose%2C%20pastel%20pink%20aesthetic%2C%20digital%20illustration%20style%2C%20clean%20simple%20background&width=400&height=400&seq=kpop-ip-001&orientation=squarish",
      "https://readdy.ai/api/search-image?query=kpop%20idol%20character%20in%20stage%20costume%2C%20pink%20sparkle%20outfit%2C%20microphone%2C%20spotlight%2C%20concert%20stage%20background%2C%20digital%20art%20style&width=400&height=400&seq=kpop-ip-002&orientation=squarish",
      "https://readdy.ai/api/search-image?query=kpop%20idol%20character%20casual%20street%20fashion%2C%20soft%20pink%20tones%2C%20urban%20background%2C%20modern%20korean%20style%2C%20digital%20illustration&width=400&height=400&seq=kpop-ip-003&orientation=squarish",
      "https://readdy.ai/api/search-image?query=kpop%20idol%20character%20in%20music%20video%20scene%2C%20dreamy%20fantasy%20setting%2C%20flowing%20white%20dress%2C%20ethereal%20light%2C%20digital%20art&width=400&height=400&seq=kpop-ip-004&orientation=squarish",
    ],
  },
  {
    id: "artist-002",
    name: "JUNO",
    groupName: "NEXUS",
    debutYear: 2020,
    category: "kpop",
    categoryLabel: "K-POP 아이돌",
    tagline: "K-Pop Idol & Choreographer",
    bio: "NEXUS의 메인 댄서 JUNO. 폭발적인 에너지와 칼군무로 무대를 장악하는 퍼포머. 팬들과 함께 안무를 만들고 싶어요.",
    avatar: "https://readdy.ai/api/search-image?query=handsome%20korean%20male%20kpop%20idol%20portrait%2C%20charismatic%20expression%2C%20dark%20dramatic%20lighting%2C%20professional%20idol%20photoshoot%2C%20clean%20background%2C%20modern%20fashion&width=200&height=200&seq=kpop-avatar-002&orientation=squarish",
    coverImage: "https://readdy.ai/api/search-image?query=kpop%20male%20idol%20dance%20performance%2C%20dramatic%20dark%20stage%20lighting%2C%20neon%20blue%20and%20white%20lights%2C%20powerful%20choreography%2C%20wide%20panoramic%20concert%20stage%2C%20energetic%20atmosphere&width=1400&height=600&seq=kpop-cover-002&orientation=landscape",
    stats: { followers: "2.8M", ipCollections: 22, completedQuests: 3654 },
    verified: true,
    genre: ["안무", "퍼포먼스", "뮤직비디오"],
    accentColor: "#4fc3f7",
    secondaryColor: "#0288d1",
    ipImages: [
      "https://readdy.ai/api/search-image?query=kpop%20male%20idol%20character%20in%20black%20stage%20outfit%2C%20dynamic%20dance%20pose%2C%20blue%20neon%20lighting%2C%20concert%20stage%20background%2C%20digital%20illustration&width=400&height=400&seq=kpop-ip-005&orientation=squarish",
      "https://readdy.ai/api/search-image?query=kpop%20male%20idol%20character%20in%20streetwear%2C%20urban%20hip%20hop%20style%2C%20blue%20accent%20colors%2C%20city%20background%2C%20digital%20art%20style&width=400&height=400&seq=kpop-ip-006&orientation=squarish",
      "https://readdy.ai/api/search-image?query=kpop%20male%20idol%20character%20in%20white%20suit%2C%20elegant%20formal%20style%2C%20spotlight%2C%20clean%20background%2C%20digital%20illustration&width=400&height=400&seq=kpop-ip-007&orientation=squarish",
      "https://readdy.ai/api/search-image?query=kpop%20male%20idol%20character%20in%20music%20video%20costume%2C%20futuristic%20outfit%2C%20blue%20holographic%20effects%2C%20digital%20art&width=400&height=400&seq=kpop-ip-008&orientation=squarish",
    ],
  },
  {
    id: "artist-003",
    name: "스튜디오 블루문",
    groupName: "드라마 제작사",
    debutYear: 2018,
    category: "drama",
    categoryLabel: "드라마 제작사",
    tagline: "감동적인 이야기를 만드는 드라마 스튜디오",
    bio: "스튜디오 블루문은 한국을 대표하는 드라마 제작사입니다. 팬들과 함께 드라마 명장면을 재현하고, 새로운 스핀오프 신을 AI로 제작해보세요.",
    avatar: "https://readdy.ai/api/search-image?query=korean%20drama%20production%20studio%20logo%20concept%2C%20elegant%20moon%20and%20film%20reel%20design%2C%20dark%20blue%20and%20gold%20color%20scheme%2C%20professional%20entertainment%20company%20branding%2C%20clean%20background&width=200&height=200&seq=drama-avatar-001&orientation=squarish",
    coverImage: "https://readdy.ai/api/search-image?query=korean%20drama%20filming%20set%2C%20cinematic%20lighting%2C%20actors%20in%20period%20costume%2C%20professional%20film%20crew%2C%20wide%20panoramic%20shot%2C%20emotional%20dramatic%20atmosphere%2C%20warm%20golden%20tones&width=1400&height=600&seq=drama-cover-001&orientation=landscape",
    stats: { followers: "1.8M", ipCollections: 45, completedQuests: 7230 },
    verified: true,
    genre: ["드라마 신 재현", "스핀오프", "OST 영상"],
    accentColor: "#3d9be9",
    secondaryColor: "#1a5fa8",
    ipImages: [
      "https://readdy.ai/api/search-image?query=korean%20drama%20scene%20recreation%2C%20two%20actors%20emotional%20confrontation%2C%20cinematic%20lighting%2C%20indoor%20set%2C%20dramatic%20composition%2C%20digital%20illustration%20style&width=400&height=400&seq=drama-ip-001&orientation=squarish",
      "https://readdy.ai/api/search-image?query=korean%20historical%20drama%20scene%2C%20traditional%20hanbok%20costume%2C%20palace%20setting%2C%20elegant%20composition%2C%20warm%20lighting%2C%20digital%20art&width=400&height=400&seq=drama-ip-002&orientation=squarish",
      "https://readdy.ai/api/search-image?query=korean%20romance%20drama%20scene%2C%20couple%20in%20rain%2C%20emotional%20moment%2C%20cinematic%20composition%2C%20soft%20lighting%2C%20digital%20illustration&width=400&height=400&seq=drama-ip-003&orientation=squarish",
      "https://readdy.ai/api/search-image?query=korean%20thriller%20drama%20scene%2C%20tense%20atmosphere%2C%20dark%20lighting%2C%20suspenseful%20composition%2C%20digital%20art%20style&width=400&height=400&seq=drama-ip-004&orientation=squarish",
    ],
  },
  {
    id: "artist-004",
    name: "시네마 픽처스",
    groupName: "영화 제작사",
    debutYear: 2015,
    category: "film",
    categoryLabel: "영화 제작사",
    tagline: "스크린을 빛내는 한국 영화의 새로운 이름",
    bio: "시네마 픽처스는 한국 영화 산업을 이끄는 제작사입니다. 팬들과 함께 영화 포스터를 제작하고, 예고편 신을 AI로 만들어보세요.",
    avatar: "https://readdy.ai/api/search-image?query=korean%20film%20production%20company%20logo%20concept%2C%20cinema%20camera%20and%20film%20strip%20design%2C%20dark%20red%20and%20gold%20color%20scheme%2C%20professional%20movie%20studio%20branding%2C%20clean%20background&width=200&height=200&seq=film-avatar-001&orientation=squarish",
    coverImage: "https://readdy.ai/api/search-image?query=korean%20movie%20film%20set%2C%20cinematic%20wide%20shot%2C%20dramatic%20lighting%2C%20professional%20film%20production%2C%20director%20and%20crew%2C%20epic%20panoramic%20composition%2C%20dark%20atmospheric%20tones&width=1400&height=600&seq=film-cover-001&orientation=landscape",
    stats: { followers: "2.1M", ipCollections: 38, completedQuests: 5640 },
    verified: true,
    genre: ["영화 포스터", "예고편 신", "명장면 재현"],
    accentColor: "#e84545",
    secondaryColor: "#c0392b",
    ipImages: [
      "https://readdy.ai/api/search-image?query=korean%20movie%20poster%20design%2C%20dramatic%20composition%2C%20cinematic%20lighting%2C%20action%20hero%20silhouette%2C%20dark%20background%2C%20professional%20film%20poster%20style&width=400&height=400&seq=film-ip-001&orientation=squarish",
      "https://readdy.ai/api/search-image?query=korean%20film%20scene%20recreation%2C%20action%20sequence%2C%20dynamic%20composition%2C%20cinematic%20lighting%2C%20digital%20illustration%20style&width=400&height=400&seq=film-ip-002&orientation=squarish",
      "https://readdy.ai/api/search-image?query=korean%20film%20trailer%20scene%2C%20suspenseful%20moment%2C%20dramatic%20lighting%2C%20cinematic%20composition%2C%20digital%20art&width=400&height=400&seq=film-ip-003&orientation=squarish",
      "https://readdy.ai/api/search-image?query=korean%20film%20noir%20scene%2C%20dark%20atmospheric%20lighting%2C%20mysterious%20composition%2C%20black%20and%20white%20tones%2C%20digital%20illustration&width=400&height=400&seq=film-ip-004&orientation=squarish",
    ],
  },
  {
    id: "artist-005",
    name: "YUNA",
    groupName: "AURORA",
    debutYear: 2021,
    category: "kpop",
    categoryLabel: "K-POP 아이돌",
    tagline: "K-Pop Idol & Dancer",
    bio: "AURORA의 메인 댄서 YUNA. 섬세하고 우아한 춤선으로 무대를 빛내는 아이돌. 팬들과 함께 안무 영상을 만들어가요.",
    avatar: "https://readdy.ai/api/search-image?query=graceful%20korean%20female%20kpop%20idol%20portrait%2C%20soft%20elegant%20makeup%2C%20lavender%20and%20white%20lighting%2C%20professional%20idol%20photoshoot%2C%20dreamy%20aesthetic&width=200&height=200&seq=kpop-avatar-005&orientation=squarish",
    coverImage: "https://readdy.ai/api/search-image?query=kpop%20female%20idol%20dance%20performance%2C%20purple%20and%20lavender%20lighting%2C%20elegant%20choreography%2C%20wide%20panoramic%20concert%20stage%2C%20graceful%20feminine%20aesthetic%2C%20flower%20petals&width=1400&height=600&seq=kpop-cover-005&orientation=landscape",
    stats: { followers: "2.4M", ipCollections: 24, completedQuests: 3891 },
    verified: true,
    genre: ["안무", "뮤직비디오", "퍼포먼스"],
    accentColor: "#c77dff",
    secondaryColor: "#e0aaff",
    ipImages: [
      "https://readdy.ai/api/search-image?query=kpop%20female%20idol%20character%20in%20purple%20stage%20costume%2C%20elegant%20dance%20pose%2C%20lavender%20lighting%2C%20dreamy%20background%2C%20digital%20illustration&width=400&height=400&seq=kpop-ip-017&orientation=squarish",
      "https://readdy.ai/api/search-image?query=kpop%20female%20idol%20character%20in%20white%20ballet-inspired%20outfit%2C%20graceful%20pose%2C%20purple%20accent%20lighting%2C%20clean%20background%2C%20digital%20art&width=400&height=400&seq=kpop-ip-018&orientation=squarish",
      "https://readdy.ai/api/search-image?query=kpop%20female%20idol%20character%20in%20casual%20purple%20outfit%2C%20modern%20korean%20fashion%2C%20soft%20lighting%2C%20urban%20background%2C%20digital%20illustration&width=400&height=400&seq=kpop-ip-019&orientation=squarish",
      "https://readdy.ai/api/search-image?query=kpop%20female%20idol%20character%20in%20music%20video%20scene%2C%20fantasy%20setting%2C%20purple%20and%20white%20tones%2C%20magical%20atmosphere%2C%20digital%20art&width=400&height=400&seq=kpop-ip-020&orientation=squarish",
    ],
  },
  {
    id: "artist-006",
    name: "웹툰 스튜디오 NOVA",
    groupName: "웹툰/애니메이션",
    debutYear: 2019,
    category: "webtoon",
    categoryLabel: "웹툰 스튜디오",
    tagline: "웹툰 IP를 영상으로 살아나게 하는 스튜디오",
    bio: "NOVA는 인기 웹툰 IP를 보유한 스튜디오입니다. 팬들과 함께 웹툰 캐릭터의 실사 영상을 AI로 제작하고, 애니메이션 신을 만들어보세요.",
    avatar: "https://readdy.ai/api/search-image?query=webtoon%20animation%20studio%20logo%20concept%2C%20colorful%20star%20and%20comic%20panel%20design%2C%20vibrant%20orange%20and%20teal%20color%20scheme%2C%20creative%20entertainment%20branding%2C%20clean%20background&width=200&height=200&seq=webtoon-avatar-001&orientation=squarish",
    coverImage: "https://readdy.ai/api/search-image?query=webtoon%20animation%20production%20studio%2C%20colorful%20character%20designs%2C%20vibrant%20digital%20art%20workspace%2C%20wide%20panoramic%20composition%2C%20creative%20atmosphere%2C%20orange%20and%20teal%20color%20scheme&width=1400&height=600&seq=webtoon-cover-001&orientation=landscape",
    stats: { followers: "1.5M", ipCollections: 32, completedQuests: 4120 },
    verified: true,
    genre: ["실사 영상화", "애니메이션 신", "캐릭터 포스터"],
    accentColor: "#ff8c42",
    secondaryColor: "#ff6b1a",
    ipImages: [
      "https://readdy.ai/api/search-image?query=webtoon%20character%20live%20action%20recreation%2C%20colorful%20costume%2C%20dynamic%20pose%2C%20vibrant%20background%2C%20digital%20illustration%20style&width=400&height=400&seq=webtoon-ip-001&orientation=squarish",
      "https://readdy.ai/api/search-image?query=webtoon%20animation%20scene%2C%20colorful%20characters%2C%20action%20sequence%2C%20vibrant%20digital%20art%20style%2C%20comic%20panel%20composition&width=400&height=400&seq=webtoon-ip-002&orientation=squarish",
      "https://readdy.ai/api/search-image?query=webtoon%20character%20poster%20design%2C%20hero%20pose%2C%20dramatic%20lighting%2C%20colorful%20background%2C%20digital%20art&width=400&height=400&seq=webtoon-ip-003&orientation=squarish",
      "https://readdy.ai/api/search-image?query=webtoon%20romance%20scene%2C%20two%20characters%2C%20soft%20lighting%2C%20colorful%20illustration%20style%2C%20emotional%20moment&width=400&height=400&seq=webtoon-ip-004&orientation=squarish",
    ],
  },
];

export const allQuests: Quest[] = [
  // SEOL quests (K-POP)
  {
    id: "quest-001",
    artistId: "artist-001",
    title: "SEOL의 벚꽃 뮤직비디오 신 제작",
    description: "SEOL이 벚꽃 흩날리는 공원에서 노래하는 뮤직비디오 오프닝 신을 AI로 제작하세요. 청량하고 감성적인 분위기를 살려주세요.",
    difficulty: "Easy",
    difficultyStars: 1,
    reward: 500,
    rewardType: "XP",
    questType: "mv",
    thumbnail: "https://readdy.ai/api/search-image?query=kpop%20female%20idol%20in%20cherry%20blossom%20park%2C%20pink%20petals%20falling%2C%20soft%20spring%20lighting%2C%20music%20video%20scene%2C%20cinematic%20wide%20shot%2C%20dreamy%20pastel%20aesthetic&width=600&height=400&seq=kpop-quest-001&orientation=landscape",
    progress: 74,
    participants: 1243,
    participantAvatars: [
      "https://readdy.ai/api/search-image?query=young%20korean%20woman%20portrait%2C%20casual%20style%2C%20bright%20smile%2C%20neutral%20background&width=40&height=40&seq=kpop-pav-001&orientation=squarish",
      "https://readdy.ai/api/search-image?query=young%20korean%20man%20portrait%2C%20casual%20style%2C%20friendly%20expression%2C%20neutral%20background&width=40&height=40&seq=kpop-pav-002&orientation=squarish",
      "https://readdy.ai/api/search-image?query=young%20woman%20portrait%2C%20kpop%20fan%20style%2C%20colorful%20background&width=40&height=40&seq=kpop-pav-003&orientation=squarish",
    ],
    deadline: "3일 남음",
    tag: "뮤직비디오",
    color: "magenta",
    presets: { tool: "seedance", shotSize: "wide", cameraAngle: "low-angle", cameraMove: "dolly-in", lighting: "golden-hour", timeOfDay: "sunset", duration: "15초" },
  },
  {
    id: "quest-002",
    artistId: "artist-001",
    title: "SEOL 발라드 감성 클로즈업 신",
    description: "SEOL의 신곡 발라드 뮤직비디오 클로즈업 신을 제작하세요. 감정이 담긴 눈빛과 섬세한 표정 연기를 AI로 표현해보세요.",
    difficulty: "Medium",
    difficultyStars: 3,
    reward: 1200,
    rewardType: "XP",
    questType: "mv",
    thumbnail: "https://readdy.ai/api/search-image?query=kpop%20female%20idol%20close%20up%20face%2C%20emotional%20expression%2C%20soft%20studio%20lighting%2C%20music%20video%20style%2C%20cinematic%20portrait%2C%20pastel%20tones%2C%20tearful%20eyes&width=600&height=400&seq=kpop-quest-002&orientation=landscape",
    progress: 48,
    participants: 687,
    participantAvatars: [
      "https://readdy.ai/api/search-image?query=kpop%20fan%20portrait%2C%20enthusiastic%20style%2C%20colorful%20background&width=40&height=40&seq=kpop-pav-004&orientation=squarish",
      "https://readdy.ai/api/search-image?query=young%20woman%20fan%20portrait%2C%20artistic%20style%2C%20neutral%20background&width=40&height=40&seq=kpop-pav-005&orientation=squarish",
      "https://readdy.ai/api/search-image?query=young%20man%20portrait%2C%20music%20fan%20style%2C%20dark%20background&width=40&height=40&seq=kpop-pav-006&orientation=squarish",
    ],
    deadline: "7일 남음",
    tag: "뮤직비디오",
    color: "magenta",
    presets: { tool: "runway", shotSize: "close-up", cameraAngle: "eye-level", cameraMove: "static", lens: "telephoto", lighting: "studio", timeOfDay: "night", duration: "10초" },
  },
  // JUNO quests (K-POP)
  {
    id: "quest-003",
    artistId: "artist-002",
    title: "JUNO 칼군무 안무 영상 제작",
    description: "NEXUS JUNO의 신곡 안무 중 포인트 안무 구간을 AI로 제작하세요. 칼같은 군무와 강렬한 에너지를 표현해보세요.",
    difficulty: "Easy",
    difficultyStars: 1,
    reward: 600,
    rewardType: "XP",
    questType: "dance",
    thumbnail: "https://readdy.ai/api/search-image?query=kpop%20male%20idol%20synchronized%20group%20dance%2C%20powerful%20choreography%2C%20blue%20neon%20stage%20lighting%2C%20concert%20performance%2C%20dynamic%20motion%2C%20wide%20shot&width=600&height=400&seq=kpop-quest-004&orientation=landscape",
    progress: 62,
    participants: 987,
    participantAvatars: [
      "https://readdy.ai/api/search-image?query=young%20kpop%20fan%20portrait%2C%20energetic%20style%2C%20colorful%20background&width=40&height=40&seq=kpop-pav-010&orientation=squarish",
      "https://readdy.ai/api/search-image?query=dance%20fan%20portrait%2C%20sporty%20style%2C%20gym%20background&width=40&height=40&seq=kpop-pav-011&orientation=squarish",
      "https://readdy.ai/api/search-image?query=young%20man%20dance%20fan%20portrait%2C%20casual%20style%2C%20neutral%20background&width=40&height=40&seq=kpop-pav-012&orientation=squarish",
    ],
    deadline: "5일 남음",
    tag: "안무",
    color: "cyan",
    presets: { tool: "kling", shotSize: "wide", cameraAngle: "low-angle", cameraMove: "orbit", lens: "wide", lighting: "neon", timeOfDay: "night", duration: "15초" },
  },
  // Drama quests
  {
    id: "quest-004",
    artistId: "artist-003",
    title: "블루문 드라마 명장면 재현",
    description: "스튜디오 블루문의 인기 드라마 클라이맥스 신을 AI로 재현하세요. 배우들의 감정 연기와 세트 분위기를 살려주세요.",
    difficulty: "Easy",
    difficultyStars: 2,
    reward: 700,
    rewardType: "XP",
    questType: "scene",
    thumbnail: "https://readdy.ai/api/search-image?query=korean%20drama%20emotional%20climax%20scene%2C%20two%20actors%20intense%20confrontation%2C%20cinematic%20indoor%20lighting%2C%20dramatic%20composition%2C%20professional%20film%20set%20atmosphere&width=600&height=400&seq=drama-quest-001&orientation=landscape",
    progress: 81,
    participants: 1567,
    participantAvatars: [
      "https://readdy.ai/api/search-image?query=drama%20fan%20portrait%2C%20passionate%20style%2C%20dark%20background&width=40&height=40&seq=drama-pav-001&orientation=squarish",
      "https://readdy.ai/api/search-image?query=young%20woman%20drama%20fan%20portrait%2C%20stylish%2C%20colorful%20background&width=40&height=40&seq=drama-pav-002&orientation=squarish",
      "https://readdy.ai/api/search-image?query=creative%20fan%20portrait%2C%20artistic%20style%2C%20studio%20background&width=40&height=40&seq=drama-pav-003&orientation=squarish",
    ],
    deadline: "2일 남음",
    tag: "드라마 신",
    color: "cyan",
    presets: { tool: "runway", shotSize: "medium", cameraAngle: "eye-level", cameraMove: "dolly-in", lens: "normal", lighting: "dramatic", timeOfDay: "night", weather: "rain", duration: "15초" },
  },
  {
    id: "quest-005",
    artistId: "artist-003",
    title: "사극 드라마 스핀오프 신 제작",
    description: "블루문의 인기 사극 드라마 스핀오프 신을 AI로 제작하세요. 한복 의상과 궁궐 배경을 활용한 새로운 이야기를 만들어보세요.",
    difficulty: "Medium",
    difficultyStars: 3,
    reward: 1500,
    rewardType: "XP",
    questType: "scene",
    thumbnail: "https://readdy.ai/api/search-image?query=korean%20historical%20drama%20scene%2C%20traditional%20hanbok%20costume%2C%20palace%20setting%2C%20elegant%20composition%2C%20warm%20golden%20lighting%2C%20cinematic%20wide%20shot&width=600&height=400&seq=drama-quest-002&orientation=landscape",
    progress: 43,
    participants: 723,
    participantAvatars: [
      "https://readdy.ai/api/search-image?query=history%20drama%20fan%20portrait%2C%20elegant%20style%2C%20studio%20background&width=40&height=40&seq=drama-pav-004&orientation=squarish",
      "https://readdy.ai/api/search-image?query=young%20woman%20fan%20portrait%2C%20traditional%20style%2C%20soft%20background&width=40&height=40&seq=drama-pav-005&orientation=squarish",
      "https://readdy.ai/api/search-image?query=creative%20director%20fan%20portrait%2C%20professional%20style%2C%20dark%20background&width=40&height=40&seq=drama-pav-006&orientation=squarish",
    ],
    deadline: "9일 남음",
    tag: "스핀오프",
    color: "cyan",
    presets: { tool: "sora", shotSize: "extreme-wide", cameraAngle: "high-angle", cameraMove: "crane", lens: "wide", lighting: "golden-hour", timeOfDay: "morning", weather: "clear", duration: "30초" },
  },
  {
    id: "quest-006",
    artistId: "artist-003",
    title: "드라마 OST 뮤직비디오 제작",
    description: "블루문 드라마의 OST 뮤직비디오를 AI로 제작하세요. 드라마의 감동적인 장면들과 OST를 연결하는 영상을 만들어보세요.",
    difficulty: "Hard",
    difficultyStars: 5,
    reward: 4500,
    rewardType: "XP",
    questType: "mv",
    thumbnail: "https://readdy.ai/api/search-image?query=korean%20drama%20OST%20music%20video%2C%20emotional%20montage%2C%20cinematic%20scenes%2C%20romantic%20atmosphere%2C%20soft%20lighting%2C%20wide%20panoramic%20composition&width=600&height=400&seq=drama-quest-003&orientation=landscape",
    progress: 19,
    participants: 234,
    participantAvatars: [
      "https://readdy.ai/api/search-image?query=creative%20fan%20portrait%2C%20artistic%20style%2C%20studio%20background&width=40&height=40&seq=drama-pav-007&orientation=squarish",
      "https://readdy.ai/api/search-image?query=filmmaker%20fan%20portrait%2C%20professional%20style%2C%20dark%20background&width=40&height=40&seq=drama-pav-008&orientation=squarish",
      "https://readdy.ai/api/search-image?query=video%20editor%20portrait%2C%20creative%20workspace%20background&width=40&height=40&seq=drama-pav-009&orientation=squarish",
    ],
    deadline: "14일 남음",
    tag: "OST 영상",
    color: "cyan",
    presets: { tool: "sora", shotSize: "wide", cameraAngle: "dutch-angle", cameraMove: "handheld", lighting: "blue-hour", timeOfDay: "sunset", weather: "cloudy", duration: "30초" },
  },
  // Film quests
  {
    id: "quest-007",
    artistId: "artist-004",
    title: "영화 포스터 AI 제작 챌린지",
    description: "시네마 픽처스의 신작 영화 포스터를 AI로 제작하세요. 영화의 장르와 분위기를 담은 임팩트 있는 포스터를 만들어보세요.",
    difficulty: "Easy",
    difficultyStars: 2,
    reward: 800,
    rewardType: "XP",
    questType: "poster",
    thumbnail: "https://readdy.ai/api/search-image?query=korean%20movie%20poster%20design%2C%20dramatic%20cinematic%20composition%2C%20hero%20silhouette%2C%20dark%20atmospheric%20background%2C%20professional%20film%20poster%20style%2C%20red%20and%20gold%20tones&width=600&height=400&seq=film-quest-001&orientation=landscape",
    progress: 67,
    participants: 1089,
    participantAvatars: [
      "https://readdy.ai/api/search-image?query=film%20fan%20portrait%2C%20cinematic%20style%2C%20dark%20background&width=40&height=40&seq=film-pav-001&orientation=squarish",
      "https://readdy.ai/api/search-image?query=young%20designer%20portrait%2C%20creative%20style%2C%20studio%20background&width=40&height=40&seq=film-pav-002&orientation=squarish",
      "https://readdy.ai/api/search-image?query=movie%20enthusiast%20portrait%2C%20passionate%20style%2C%20neutral%20background&width=40&height=40&seq=film-pav-003&orientation=squarish",
    ],
    deadline: "6일 남음",
    tag: "영화 포스터",
    color: "orange",
    presets: { tool: "pika", shotSize: "extreme-wide", cameraAngle: "low-angle", lens: "wide", lighting: "dramatic", timeOfDay: "midnight", duration: "5초" },
  },
  {
    id: "quest-008",
    artistId: "artist-004",
    title: "영화 예고편 핵심 신 제작",
    description: "시네마 픽처스 신작의 예고편에 들어갈 핵심 신을 AI로 제작하세요. 관객의 기대감을 높이는 임팩트 있는 장면을 만들어보세요.",
    difficulty: "Medium",
    difficultyStars: 3,
    reward: 2000,
    rewardType: "XP",
    questType: "trailer",
    thumbnail: "https://readdy.ai/api/search-image?query=korean%20movie%20trailer%20scene%2C%20action%20sequence%2C%20dramatic%20lighting%2C%20cinematic%20composition%2C%20suspenseful%20atmosphere%2C%20wide%20shot&width=600&height=400&seq=film-quest-002&orientation=landscape",
    progress: 35,
    participants: 456,
    participantAvatars: [
      "https://readdy.ai/api/search-image?query=film%20director%20fan%20portrait%2C%20professional%20style%2C%20studio%20background&width=40&height=40&seq=film-pav-004&orientation=squarish",
      "https://readdy.ai/api/search-image?query=young%20cinematographer%20portrait%2C%20creative%20style%2C%20dark%20background&width=40&height=40&seq=film-pav-005&orientation=squarish",
      "https://readdy.ai/api/search-image?query=movie%20fan%20portrait%2C%20passionate%20style%2C%20cinema%20background&width=40&height=40&seq=film-pav-006&orientation=squarish",
    ],
    deadline: "10일 남음",
    tag: "예고편",
    color: "orange",
    presets: { tool: "sora", shotSize: "wide", cameraAngle: "low-angle", cameraMove: "dolly-out", lens: "telephoto", lighting: "dramatic", timeOfDay: "night", weather: "storm", duration: "30초" },
  },
  // YUNA quests (K-POP)
  {
    id: "quest-009",
    artistId: "artist-005",
    title: "YUNA 우아한 안무 영상 제작",
    description: "YUNA의 신곡 안무 중 가장 우아한 구간을 AI로 제작하세요. 섬세한 춤선과 감성적인 표현을 담아보세요.",
    difficulty: "Easy",
    difficultyStars: 2,
    reward: 550,
    rewardType: "XP",
    questType: "dance",
    thumbnail: "https://readdy.ai/api/search-image?query=kpop%20female%20idol%20graceful%20dance%20performance%2C%20purple%20lavender%20lighting%2C%20elegant%20choreography%2C%20flowing%20dress%2C%20cinematic%20wide%20shot%2C%20dreamy%20atmosphere&width=600&height=400&seq=kpop-quest-012&orientation=landscape",
    progress: 77,
    participants: 1089,
    participantAvatars: [
      "https://readdy.ai/api/search-image?query=dance%20fan%20portrait%2C%20graceful%20style%2C%20purple%20background&width=40&height=40&seq=kpop-pav-034&orientation=squarish",
      "https://readdy.ai/api/search-image?query=young%20woman%20fan%20portrait%2C%20elegant%20style%2C%20soft%20background&width=40&height=40&seq=kpop-pav-035&orientation=squarish",
      "https://readdy.ai/api/search-image?query=ballet%20fan%20portrait%2C%20artistic%20style%2C%20studio%20background&width=40&height=40&seq=kpop-pav-036&orientation=squarish",
    ],
    deadline: "6일 남음",
    tag: "안무",
    color: "purple",
    presets: { tool: "seedance", shotSize: "wide", cameraAngle: "eye-level", cameraMove: "pan", lens: "normal", lighting: "moonlight", timeOfDay: "night", duration: "15초" },
  },
  // Webtoon quests
  {
    id: "quest-010",
    artistId: "artist-006",
    title: "웹툰 캐릭터 실사 영상화",
    description: "NOVA 스튜디오의 인기 웹툰 주인공을 실사 영상으로 AI 제작하세요. 웹툰의 감성을 살린 생동감 있는 영상을 만들어보세요.",
    difficulty: "Easy",
    difficultyStars: 2,
    reward: 650,
    rewardType: "XP",
    questType: "scene",
    thumbnail: "https://readdy.ai/api/search-image?query=webtoon%20character%20live%20action%20recreation%2C%20colorful%20costume%2C%20dynamic%20pose%2C%20vibrant%20background%2C%20cinematic%20composition%2C%20digital%20art%20style&width=600&height=400&seq=webtoon-quest-001&orientation=landscape",
    progress: 58,
    participants: 834,
    participantAvatars: [
      "https://readdy.ai/api/search-image?query=webtoon%20fan%20portrait%2C%20colorful%20style%2C%20vibrant%20background&width=40&height=40&seq=webtoon-pav-001&orientation=squarish",
      "https://readdy.ai/api/search-image?query=young%20animator%20portrait%2C%20creative%20style%2C%20studio%20background&width=40&height=40&seq=webtoon-pav-002&orientation=squarish",
      "https://readdy.ai/api/search-image?query=webtoon%20enthusiast%20portrait%2C%20vibrant%20style%2C%20colorful%20background&width=40&height=40&seq=webtoon-pav-003&orientation=squarish",
    ],
    deadline: "4일 남음",
    tag: "웹툰 실사화",
    color: "yellow",
    presets: { tool: "pika", shotSize: "medium", cameraAngle: "eye-level", cameraMove: "orbit", lens: "normal", lighting: "studio", timeOfDay: "noon", weather: "clear", duration: "15초" },
  },
  {
    id: "quest-011",
    artistId: "artist-006",
    title: "웹툰 애니메이션 오프닝 신",
    description: "NOVA 웹툰의 애니메이션 오프닝 신을 AI로 제작하세요. 웹툰 특유의 컬러풀한 감성과 역동적인 연출을 표현해보세요.",
    difficulty: "Medium",
    difficultyStars: 3,
    reward: 1800,
    rewardType: "XP",
    questType: "trailer",
    thumbnail: "https://readdy.ai/api/search-image?query=webtoon%20animation%20opening%20sequence%2C%20colorful%20characters%2C%20dynamic%20action%2C%20vibrant%20digital%20art%20style%2C%20wide%20cinematic%20composition%2C%20orange%20and%20teal%20color%20scheme&width=600&height=400&seq=webtoon-quest-002&orientation=landscape",
    progress: 39,
    participants: 612,
    participantAvatars: [
      "https://readdy.ai/api/search-image?query=animation%20fan%20portrait%2C%20creative%20style%2C%20colorful%20background&width=40&height=40&seq=webtoon-pav-004&orientation=squarish",
      "https://readdy.ai/api/search-image?query=young%20illustrator%20portrait%2C%20artistic%20style%2C%20studio%20background&width=40&height=40&seq=webtoon-pav-005&orientation=squarish",
      "https://readdy.ai/api/search-image?query=webtoon%20enthusiast%20portrait%2C%20vibrant%20style%2C%20colorful%20background&width=40&height=40&seq=webtoon-pav-006&orientation=squarish",
    ],
    deadline: "11일 남음",
    tag: "애니메이션",
    color: "yellow",
    presets: { tool: "kling", shotSize: "extreme-wide", cameraAngle: "birds-eye", cameraMove: "crane", lens: "fisheye", lighting: "neon", timeOfDay: "night", duration: "30초" },
  },
  {
    id: "quest-012",
    artistId: "artist-006",
    title: "웹툰 캐릭터 포스터 제작",
    description: "NOVA 스튜디오의 인기 웹툰 캐릭터 공식 포스터를 AI로 제작하세요. 캐릭터의 개성과 매력을 담은 임팩트 있는 포스터를 만들어보세요.",
    difficulty: "Hard",
    difficultyStars: 4,
    reward: 3500,
    rewardType: "XP",
    questType: "poster",
    thumbnail: "https://readdy.ai/api/search-image?query=webtoon%20character%20official%20poster%2C%20hero%20pose%2C%20dramatic%20lighting%2C%20colorful%20vibrant%20background%2C%20professional%20poster%20design%2C%20digital%20art%20style&width=600&height=400&seq=webtoon-quest-003&orientation=landscape",
    progress: 22,
    participants: 287,
    participantAvatars: [
      "https://readdy.ai/api/search-image?query=graphic%20designer%20portrait%2C%20professional%20style%2C%20studio%20background&width=40&height=40&seq=webtoon-pav-007&orientation=squarish",
      "https://readdy.ai/api/search-image?query=creative%20director%20portrait%2C%20artistic%20style%2C%20dark%20background&width=40&height=40&seq=webtoon-pav-008&orientation=squarish",
      "https://readdy.ai/api/search-image?query=illustrator%20portrait%2C%20creative%20workspace%2C%20colorful%20background&width=40&height=40&seq=webtoon-pav-009&orientation=squarish",
    ],
    deadline: "16일 남음",
    tag: "캐릭터 포스터",
    color: "yellow",
    presets: { tool: "sora", shotSize: "medium", cameraAngle: "low-angle", lens: "telephoto", lighting: "dramatic", timeOfDay: "midnight", duration: "10초" },
  },
];

export const artistRewards: Reward[] = [
  // SEOL (artist-001) - K-POP
  {
    id: "reward-001",
    artistId: "artist-001",
    title: "SEOL 팬미팅 초청권",
    description: "SEOL과 직접 만나는 특별한 팬미팅에 초대됩니다. 사인회 및 포토타임 포함.",
    category: "offline",
    categoryLabel: "오프라인 이벤트",
    xpCost: 50000,
    stock: 5,
    totalStock: 20,
    icon: "ri-user-heart-line",
    color: "#ff6eb4",
    badge: "ULTRA RARE",
    startDate: "2026-04-01",
    endDate: "2026-06-30",
    image: "https://readdy.ai/api/search-image?query=kpop%20fan%20meeting%20event%2C%20idol%20and%20fans%2C%20warm%20pink%20lighting%2C%20intimate%20venue%20setting%2C%20excited%20fans%20with%20banners%2C%20professional%20event%20photography%20style%2C%20elegant%20atmosphere&width=600&height=400&seq=reward-001&orientation=landscape",
    perks: ["팬미팅 입장권", "2샷 포토카드", "사인 CD", "굿즈 패키지"],
    isLimited: true,
  },
  {
    id: "reward-002",
    artistId: "artist-001",
    title: "뮤직비디오 크레딧 등재",
    description: "SEOL의 다음 뮤직비디오 엔딩 크레딧에 당신의 이름이 올라갑니다.",
    category: "credit",
    categoryLabel: "크레딧 등재",
    xpCost: 20000,
    stock: 50,
    totalStock: 100,
    icon: "ri-film-line",
    color: "#ffd60a",
    badge: "RARE",
    startDate: "2026-03-15",
    endDate: "2026-07-31",
    image: "https://readdy.ai/api/search-image?query=music%20video%20production%20behind%20the%20scenes%2C%20film%20credits%20rolling%20on%20screen%2C%20cinematic%20atmosphere%2C%20pink%20and%20gold%20tones%2C%20professional%20production%20setting&width=600&height=400&seq=reward-002&orientation=landscape",
    perks: ["엔딩 크레딧 이름 등재", "크레딧 스크린샷 디지털 증명서", "비하인드 영상 독점 공개"],
    isLimited: false,
  },
  {
    id: "reward-003",
    artistId: "artist-001",
    title: "SEOL 디지털 사인 포토카드 팩",
    description: "SEOL의 직접 사인이 담긴 한정판 디지털 포토카드 5종 세트.",
    category: "digital",
    categoryLabel: "디지털 굿즈",
    xpCost: 5000,
    stock: 200,
    totalStock: 500,
    icon: "ri-polaroid-2-line",
    color: "#ff6eb4",
    startDate: "2026-04-05",
    endDate: "2026-08-31",
    image: "https://readdy.ai/api/search-image?query=kpop%20idol%20digital%20photocard%20collection%2C%20elegant%20portrait%20cards%20with%20signature%2C%20pink%20aesthetic%2C%20limited%20edition%20design%2C%20high%20quality%20digital%20art&width=600&height=400&seq=reward-003&orientation=landscape",
    perks: ["디지털 포토카드 5종", "고해상도 다운로드", "배경화면 버전 포함"],
    isLimited: false,
  },
  {
    id: "reward-004",
    artistId: "artist-001",
    title: "SEOL 비하인드 독점 영상 접근권",
    description: "뮤직비디오 촬영 비하인드, 연습실 영상 등 독점 콘텐츠를 30일간 무제한 시청.",
    category: "exclusive",
    categoryLabel: "독점 콘텐츠",
    xpCost: 8000,
    stock: 150,
    totalStock: 300,
    icon: "ri-lock-unlock-line",
    color: "#c77dff",
    startDate: "2026-04-10",
    endDate: "2026-09-30",
    image: "https://readdy.ai/api/search-image?query=kpop%20idol%20behind%20the%20scenes%20filming%2C%20practice%20room%2C%20exclusive%20content%2C%20warm%20intimate%20lighting%2C%20candid%20moments%2C%20professional%20photography&width=600&height=400&seq=reward-004&orientation=landscape",
    perks: ["비하인드 영상 30일 접근", "연습실 독점 영상", "미공개 셀카 앨범"],
    isLimited: false,
  },

  // JUNO (artist-002) - K-POP
  {
    id: "reward-005",
    artistId: "artist-002",
    title: "JUNO 안무 마스터클래스 초청",
    description: "JUNO와 함께하는 안무 마스터클래스에 참가하세요. 직접 안무를 배우는 특별한 경험.",
    category: "offline",
    categoryLabel: "오프라인 이벤트",
    xpCost: 45000,
    stock: 8,
    totalStock: 30,
    icon: "ri-body-scan-line",
    color: "#4fc3f7",
    badge: "ULTRA RARE",
    startDate: "2026-04-01",
    endDate: "2026-07-15",
    image: "https://readdy.ai/api/search-image?query=kpop%20dance%20masterclass%2C%20idol%20teaching%20dance%20to%20fans%2C%20practice%20studio%2C%20blue%20neon%20lighting%2C%20energetic%20atmosphere%2C%20professional%20dance%20studio%20setting&width=600&height=400&seq=reward-005&orientation=landscape",
    perks: ["안무 마스터클래스 참가", "JUNO 1:1 피드백", "수료증 발급", "단체 기념 촬영"],
    isLimited: true,
  },
  {
    id: "reward-006",
    artistId: "artist-002",
    title: "뮤직비디오 댄서 크레딧",
    description: "JUNO의 다음 뮤직비디오에 댄서 크레딧으로 이름을 올리세요.",
    category: "credit",
    categoryLabel: "크레딧 등재",
    xpCost: 25000,
    stock: 30,
    totalStock: 50,
    icon: "ri-film-line",
    color: "#ffd60a",
    badge: "RARE",
    startDate: "2026-03-20",
    endDate: "2026-08-20",
    image: "https://readdy.ai/api/search-image?query=music%20video%20dance%20credit%2C%20behind%20the%20scenes%20dance%20filming%2C%20blue%20stage%20lighting%2C%20energetic%20production%20atmosphere%2C%20film%20credits%20display&width=600&height=400&seq=reward-006&orientation=landscape",
    perks: ["댄서 크레딧 등재", "무대 비하인드 독점 영상", "공식 스크린샷 제공"],
    isLimited: false,
  },

  // 스튜디오 블루문 (artist-003) - Drama
  {
    id: "reward-007",
    artistId: "artist-003",
    title: "드라마 시사회 VIP 초청권",
    description: "스튜디오 블루문의 신작 드라마 첫 시사회에 VIP로 초대됩니다. 배우 포토월 포함.",
    category: "offline",
    categoryLabel: "오프라인 이벤트",
    xpCost: 60000,
    stock: 3,
    totalStock: 10,
    icon: "ri-movie-2-line",
    color: "#3d9be9",
    badge: "LEGENDARY",
    startDate: "2026-04-15",
    endDate: "2026-08-01",
    image: "https://readdy.ai/api/search-image?query=korean%20drama%20premiere%20event%2C%20VIP%20red%20carpet%2C%20actors%20and%20fans%2C%20elegant%20venue%2C%20professional%20event%20photography%2C%20blue%20and%20gold%20lighting%2C%20luxury%20atmosphere&width=600&height=400&seq=reward-007&orientation=landscape",
    perks: ["시사회 VIP 입장권", "배우 포토월 참여", "공식 굿즈 패키지", "포스터 사인본"],
    isLimited: true,
  },
  {
    id: "reward-008",
    artistId: "artist-003",
    title: "드라마 촬영장 견학 투어",
    description: "드라마 촬영 세트장을 직접 방문하고 비하인드 촬영 과정을 체험하는 특별한 투어.",
    category: "offline",
    categoryLabel: "오프라인 이벤트",
    xpCost: 35000,
    stock: 12,
    totalStock: 40,
    icon: "ri-camera-line",
    color: "#3d9be9",
    badge: "RARE",
    startDate: "2026-05-01",
    endDate: "2026-07-20",
    image: "https://readdy.ai/api/search-image?query=korean%20drama%20filming%20set%20tour%2C%20behind%20the%20scenes%2C%20professional%20film%20crew%2C%20studio%20set%2C%20visitors%20watching%20production%2C%20cinematic%20atmosphere&width=600&height=400&seq=reward-008&orientation=landscape",
    perks: ["세트장 견학", "PD 비하인드 인터뷰", "소품 포토존 체험", "기념 굿즈"],
    isLimited: true,
  },
  {
    id: "reward-009",
    artistId: "artist-003",
    title: "드라마 엔딩 크레딧 등재",
    description: "블루문 드라마 최종화 엔딩 크레딧에 스페셜 팬 크리에이터로 이름이 올라갑니다.",
    category: "credit",
    categoryLabel: "크레딧 등재",
    xpCost: 15000,
    stock: 100,
    totalStock: 200,
    icon: "ri-tv-2-line",
    color: "#ffd60a",
    startDate: "2026-03-01",
    endDate: "2026-09-30",
    image: "https://readdy.ai/api/search-image?query=drama%20TV%20credits%20rolling%2C%20production%20credits%20on%20screen%2C%20cinematic%20dark%20background%2C%20elegant%20typography%2C%20korean%20drama%20ending%20credits&width=600&height=400&seq=reward-009&orientation=landscape",
    perks: ["드라마 최종화 크레딧 등재", "디지털 증명서 발급", "크레딧 영상 클립 제공"],
    isLimited: false,
  },
  {
    id: "reward-010",
    artistId: "artist-003",
    title: "독점 OST 미발매 트랙 감상권",
    description: "공개 전 OST 미발매 트랙을 독점으로 감상할 수 있는 특별 권한.",
    category: "exclusive",
    categoryLabel: "독점 콘텐츠",
    xpCost: 10000,
    stock: 80,
    totalStock: 150,
    icon: "ri-music-2-line",
    color: "#c77dff",
    startDate: "2026-04-01",
    endDate: "2026-10-31",
    image: "https://readdy.ai/api/search-image?query=exclusive%20music%20listening%20session%2C%20unreleased%20track%20preview%2C%20professional%20recording%20studio%2C%20headphones%2C%20intimate%20atmosphere%2C%20blue%20and%20purple%20lighting&width=600&height=400&seq=reward-010&orientation=landscape",
    perks: ["미발매 OST 2트랙 독점 감상", "30일 스트리밍 권한", "악보 PDF 제공"],
    isLimited: false,
  },

  // 시네마 픽처스 (artist-004) - Film
  {
    id: "reward-011",
    artistId: "artist-004",
    title: "영화 월드 프리미어 시사회 초청",
    description: "시네마 픽처스 신작 영화의 월드 프리미어에 초대됩니다. 감독 및 배우와의 GV 포함.",
    category: "offline",
    categoryLabel: "오프라인 이벤트",
    xpCost: 80000,
    stock: 2,
    totalStock: 8,
    icon: "ri-award-line",
    color: "#e84545",
    badge: "LEGENDARY",
    startDate: "2026-07-01",
    endDate: "2026-09-15",
    image: "https://readdy.ai/api/search-image?query=korean%20film%20world%20premiere%20event%2C%20red%20carpet%2C%20director%20and%20actors%2C%20luxury%20cinema%20venue%2C%20professional%20event%20photography%2C%20dramatic%20red%20and%20gold%20lighting&width=600&height=400&seq=reward-011&orientation=landscape",
    perks: ["월드 프리미어 레드카펫 입장", "감독 GV 참여권", "포스터 사인본", "기념 굿즈 세트"],
    isLimited: true,
  },
  {
    id: "reward-012",
    artistId: "artist-004",
    title: "영화 스태프 크레딧 등재",
    description: "시네마 픽처스 신작 영화 스태프 크레딧에 스페셜 팬 크리에이터로 이름이 오릅니다.",
    category: "credit",
    categoryLabel: "크레딧 등재",
    xpCost: 30000,
    stock: 50,
    totalStock: 100,
    icon: "ri-film-line",
    color: "#ffd60a",
    badge: "RARE",
    startDate: "2026-04-01",
    endDate: "2026-10-01",
    image: "https://readdy.ai/api/search-image?query=movie%20credits%20rolling%20on%20cinema%20screen%2C%20dramatic%20dark%20theater%2C%20professional%20film%20production%20credits%2C%20red%20and%20gold%20tones&width=600&height=400&seq=reward-012&orientation=landscape",
    perks: ["영화 스태프 크레딧 등재", "공식 증명서 발급", "블루레이 사인본"],
    isLimited: false,
  },
  {
    id: "reward-013",
    artistId: "artist-004",
    title: "영화 촬영 비하인드 독점 패키지",
    description: "감독 인터뷰, NG 영상, 메이킹 필름 등 공개되지 않는 독점 비하인드 콘텐츠 패키지.",
    category: "exclusive",
    categoryLabel: "독점 콘텐츠",
    xpCost: 12000,
    stock: 100,
    totalStock: 200,
    icon: "ri-video-line",
    color: "#ff8c42",
    startDate: "2026-04-05",
    endDate: "2026-11-30",
    image: "https://readdy.ai/api/search-image?query=movie%20behind%20the%20scenes%20exclusive%20content%2C%20director%20interview%2C%20NG%20footage%2C%20making%20film%2C%20professional%20production%20atmosphere%2C%20cinematic%20lighting&width=600&height=400&seq=reward-013&orientation=landscape",
    perks: ["감독 독점 인터뷰 영상", "NG 비하인드 영상", "메이킹 필름 30일 접근"],
    isLimited: false,
  },

  // YUNA (artist-005) - K-POP
  {
    id: "reward-014",
    artistId: "artist-005",
    title: "YUNA 팬사인회 초청권",
    description: "YUNA와 직접 소통하는 팬사인회에 초대됩니다. 1:1 사인 및 대화 시간 포함.",
    category: "offline",
    categoryLabel: "오프라인 이벤트",
    xpCost: 40000,
    stock: 10,
    totalStock: 30,
    icon: "ri-pen-nib-line",
    color: "#c77dff",
    badge: "ULTRA RARE",
    startDate: "2026-04-01",
    endDate: "2026-07-20",
    image: "https://readdy.ai/api/search-image?query=kpop%20fan%20sign%20event%2C%20idol%20signing%20albums%20for%20fans%2C%20purple%20and%20lavender%20lighting%2C%20intimate%20fan%20meeting%2C%20professional%20event%20atmosphere%2C%20elegant%20setting&width=600&height=400&seq=reward-014&orientation=landscape",
    perks: ["팬사인회 입장권", "1:1 사인 타임", "2샷 폴라로이드", "사인 앨범"],
    isLimited: true,
  },
  {
    id: "reward-015",
    artistId: "artist-005",
    title: "YUNA 안무 크레딧 협업자",
    description: "YUNA의 다음 안무 뮤직비디오에 크리에이티브 협업자 크레딧으로 등재됩니다.",
    category: "credit",
    categoryLabel: "크레딧 등재",
    xpCost: 22000,
    stock: 40,
    totalStock: 80,
    icon: "ri-film-line",
    color: "#ffd60a",
    badge: "RARE",
    startDate: "2026-03-10",
    endDate: "2026-08-31",
    image: "https://readdy.ai/api/search-image?query=dance%20choreography%20credit%2C%20music%20video%20production%2C%20purple%20lavender%20stage%20lighting%2C%20elegant%20dance%20performance%20filming%2C%20creative%20collaboration%20atmosphere&width=600&height=400&seq=reward-015&orientation=landscape",
    perks: ["크리에이티브 협업자 크레딧", "뮤직비디오 비하인드 독점", "공식 증명서"],
    isLimited: false,
  },

  // 웹툰 스튜디오 NOVA (artist-006)
  {
    id: "reward-016",
    artistId: "artist-006",
    title: "웹툰 IP 컬래버 크레딧",
    description: "NOVA 신작 웹툰의 스페셜 팬 크리에이터 크레딧에 이름이 올라갑니다.",
    category: "credit",
    categoryLabel: "크레딧 등재",
    xpCost: 18000,
    stock: 60,
    totalStock: 100,
    icon: "ri-book-open-line",
    color: "#ff8c42",
    badge: "RARE",
    startDate: "2026-04-01",
    endDate: "2026-09-01",
    image: "https://readdy.ai/api/search-image?query=webtoon%20production%20credits%2C%20digital%20comic%20creation%2C%20colorful%20animation%20studio%2C%20creator%20collaboration%2C%20vibrant%20orange%20and%20teal%20tones%2C%20professional%20digital%20art%20setting&width=600&height=400&seq=reward-016&orientation=landscape",
    perks: ["웹툰 스페셜 크레딧 등재", "디지털 증명서", "작가 사인 포스터"],
    isLimited: false,
  },
  {
    id: "reward-017",
    artistId: "artist-006",
    title: "NOVA 작가 팬미팅 초청",
    description: "NOVA 스튜디오 대표 작가진과의 팬미팅에 초대됩니다. 작품 비하인드 토크쇼 포함.",
    category: "offline",
    categoryLabel: "오프라인 이벤트",
    xpCost: 35000,
    stock: 15,
    totalStock: 40,
    icon: "ri-user-star-line",
    color: "#ff8c42",
    badge: "RARE",
    startDate: "2026-04-20",
    endDate: "2026-08-20",
    image: "https://readdy.ai/api/search-image?query=webtoon%20creator%20fan%20meeting%2C%20artist%20and%20fans%2C%20creative%20studio%20setting%2C%20colorful%20vibrant%20atmosphere%2C%20professional%20event%20photography%2C%20orange%20and%20teal%20lighting&width=600&height=400&seq=reward-017&orientation=landscape",
    perks: ["팬미팅 초청권", "작가 사인본", "미공개 컨셉아트 북", "스케치 엽서 세트"],
    isLimited: true,
  },
  {
    id: "reward-018",
    artistId: "artist-006",
    title: "미공개 에피소드 선공개 독점권",
    description: "NOVA 인기 웹툰의 다음 에피소드를 공식 공개 7일 전에 독점으로 먼저 감상.",
    category: "exclusive",
    categoryLabel: "독점 콘텐츠",
    xpCost: 7000,
    stock: 200,
    totalStock: 500,
    icon: "ri-eye-line",
    color: "#4fc3f7",
    startDate: "2026-04-01",
    endDate: "2026-12-31",
    image: "https://readdy.ai/api/search-image?query=webtoon%20exclusive%20preview%2C%20digital%20comic%20early%20access%2C%20tablet%20screen%20with%20colorful%20webtoon%20panels%2C%20exclusive%20member%20content%2C%20vibrant%20background&width=600&height=400&seq=reward-018&orientation=landscape",
    perks: ["다음화 7일 선공개", "작가 코멘터리 포함", "HD 고화질 버전 제공"],
    isLimited: false,
  },
];

export const myArtistXP: ArtistXP[] = [
  {
    artistId: "artist-001",
    xp: 15400,
    level: 7,
    levelName: "AURORA 팬",
    nextLevelXP: 20000,
    totalEarned: 28700,
    questsCompleted: 12,
  },
  {
    artistId: "artist-002",
    xp: 8200,
    level: 4,
    levelName: "NEXUS 팬",
    nextLevelXP: 12000,
    totalEarned: 14500,
    questsCompleted: 7,
  },
  {
    artistId: "artist-003",
    xp: 22100,
    level: 9,
    levelName: "블루문 VIP",
    nextLevelXP: 30000,
    totalEarned: 41200,
    questsCompleted: 18,
  },
  {
    artistId: "artist-004",
    xp: 5600,
    level: 3,
    levelName: "시네마 팬",
    nextLevelXP: 8000,
    totalEarned: 9800,
    questsCompleted: 5,
  },
  {
    artistId: "artist-005",
    xp: 11300,
    level: 5,
    levelName: "AURORA 팬",
    nextLevelXP: 15000,
    totalEarned: 19600,
    questsCompleted: 9,
  },
  {
    artistId: "artist-006",
    xp: 3100,
    level: 2,
    levelName: "NOVA 팬",
    nextLevelXP: 5000,
    totalEarned: 4800,
    questsCompleted: 3,
  },
];

export const artistProfile = artists[0];
export const quests = allQuests.filter((q) => q.artistId === "artist-001");

export const communityFeed = [
  {
    id: "feed-001",
    user: {
      name: "StarFan_Minjee",
      avatar: "https://readdy.ai/api/search-image?query=young%20korean%20woman%20portrait%2C%20kpop%20fan%20style%2C%20bright%20smile%2C%20neutral%20background&width=60&height=60&seq=kpop-feed-av-001&orientation=squarish",
      badge: "Top Creator",
    },
    thumbnail: "https://readdy.ai/api/search-image?query=kpop%20female%20idol%20in%20cherry%20blossom%20park%20music%20video%20scene%2C%20pink%20petals%2C%20dreamy%20atmosphere%2C%20cinematic%20composition&width=600&height=340&seq=kpop-feed-001&orientation=landscape",
    title: "SEOL 벚꽃 뮤직비디오 신 완성!",
    description: "AI로 처음 만들어본 뮤직비디오 신이에요. SEOL 언니의 벚꽃 장면을 표현했습니다. 너무 예쁘게 나왔어요!",
    duration: "0:15",
    likes: 3421,
    comments: 187,
    shares: 94,
    timeAgo: "2시간 전",
    questTag: "벚꽃 뮤직비디오 신",
    artistId: "artist-001",
    liked: false,
    bookmarked: false,
  },
  {
    id: "feed-002",
    user: {
      name: "DanceKing_Hyun",
      avatar: "https://readdy.ai/api/search-image?query=young%20korean%20man%20portrait%2C%20dance%20fan%20style%2C%20energetic%20expression%2C%20neutral%20background&width=60&height=60&seq=kpop-feed-av-002&orientation=squarish",
      badge: "Quest Master",
    },
    thumbnail: "https://readdy.ai/api/search-image?query=kpop%20male%20idol%20dance%20performance%2C%20dramatic%20dark%20stage%20lighting%2C%20neon%20blue%20and%20white%20lights%2C%20powerful%20choreography%2C%20wide%20panoramic%20concert%20stage%2C%20energetic%20atmosphere&width=600&height=340&seq=kpop-feed-002&orientation=landscape",
    title: "JUNO 칼군무 안무 영상 도전!",
    description: "JUNO 오빠 안무 퀘스트 완성! AI가 칼군무를 이렇게 잘 표현해줄 줄 몰랐어요. 진짜 실제 같아요.",
    duration: "0:20",
    likes: 5672,
    comments: 312,
    shares: 198,
    timeAgo: "5시간 전",
    questTag: "칼군무 안무",
    artistId: "artist-002",
    liked: true,
    bookmarked: true,
  },
  {
    id: "feed-003",
    user: {
      name: "DramaLover_Sora",
      avatar: "https://readdy.ai/api/search-image?query=young%20korean%20woman%20portrait%2C%20stylish%20drama%20fan%2C%20colorful%20background&width=60&height=60&seq=drama-feed-av-001&orientation=squarish",
      badge: "Rising Star",
    },
    thumbnail: "https://readdy.ai/api/search-image?query=korean%20drama%20emotional%20scene%20recreation%2C%20two%20actors%2C%20cinematic%20indoor%20lighting%2C%20dramatic%20composition%2C%20professional%20film%20atmosphere&width=600&height=340&seq=drama-feed-001&orientation=landscape",
    title: "블루문 드라마 명장면 재현 완성!",
    description: "드라마 클라이맥스 신을 AI로 재현했어요. 배우들의 감정 연기가 너무 잘 살아났어요. 감동적이에요!",
    duration: "0:18",
    likes: 4231,
    comments: 256,
    shares: 134,
    timeAgo: "8시간 전",
    questTag: "드라마 명장면",
    artistId: "artist-003",
    liked: false,
    bookmarked: false,
  },
  {
    id: "feed-004",
    user: {
      name: "FilmBuff_Jiwon",
      avatar: "https://readdy.ai/api/search-image?query=young%20korean%20man%20portrait%2C%20film%20enthusiast%20style%2C%20dark%20background&width=60&height=60&seq=film-feed-av-001&orientation=squarish",
      badge: "Explorer",
    },
    thumbnail: "https://readdy.ai/api/search-image?query=korean%20movie%20poster%20design%2C%20dramatic%20cinematic%20composition%2C%20hero%20silhouette%2C%20dark%20atmospheric%20background%2C%20professional%20film%20poster%20style&width=600&height=340&seq=film-feed-001&orientation=landscape",
    title: "시네마 픽처스 영화 포스터 제작!",
    description: "신작 영화 포스터를 AI로 만들었어요. 영화의 분위기를 담은 임팩트 있는 포스터가 완성됐어요!",
    duration: "포스터",
    likes: 3891,
    comments: 198,
    shares: 127,
    timeAgo: "1일 전",
    questTag: "영화 포스터",
    artistId: "artist-004",
    liked: false,
    bookmarked: true,
  },
  {
    id: "feed-005",
    user: {
      name: "WebtoonFan_Junho",
      avatar: "https://readdy.ai/api/search-image?query=young%20korean%20man%20portrait%2C%20webtoon%20fan%20style%2C%20colorful%20background&width=60&height=60&seq=webtoon-feed-av-001&orientation=squarish",
      badge: "Top Creator",
    },
    thumbnail: "https://readdy.ai/api/search-image?query=webtoon%20character%20live%20action%20recreation%2C%20colorful%20costume%2C%20dynamic%20pose%2C%20vibrant%20background%2C%20cinematic%20composition%2C%20digital%20art%20style&width=600&height=340&seq=webtoon-feed-001&orientation=landscape",
    title: "NOVA 웹툰 캐릭터 실사 영상화!",
    description: "웹툰 주인공을 실사 영상으로 만들었어요. AI가 웹툰 감성을 이렇게 잘 살려줄 줄 몰랐어요!",
    duration: "0:15",
    likes: 5123,
    comments: 287,
    shares: 198,
    timeAgo: "2일 전",
    questTag: "웹툰 실사화",
    artistId: "artist-006",
    liked: true,
    bookmarked: false,
  },
  {
    id: "feed-006",
    user: {
      name: "YunaFan_Nari",
      avatar: "https://readdy.ai/api/search-image?query=young%20korean%20woman%20portrait%2C%20sweet%20kpop%20fan%20style%2C%20pink%20background&width=60&height=60&seq=kpop-feed-av-006&orientation=squarish",
      badge: "Innovator",
    },
    thumbnail: "https://readdy.ai/api/search-image?query=kpop%20female%20idol%20graceful%20dance%20performance%2C%20purple%20lavender%20lighting%2C%20elegant%20choreography%2C%20flowing%20dress%2C%20dreamy%20atmosphere&width=600&height=340&seq=kpop-feed-004&orientation=landscape",
    title: "YUNA 우아한 안무 영상 완성",
    description: "YUNA 언니의 우아한 안무를 AI로 표현했어요. 보라색 조명이 너무 예쁘게 나왔어요.",
    duration: "0:18",
    likes: 6234,
    comments: 421,
    shares: 267,
    timeAgo: "3일 전",
    questTag: "우아한 안무",
    artistId: "artist-005",
    liked: false,
    bookmarked: false,
  },
];

export const leaderboard = [
  { rank: 1, name: "DanceKing_Hyun", avatar: "https://readdy.ai/api/search-image?query=young%20korean%20man%20portrait%2C%20dance%20fan%20style%2C%20energetic%20expression&width=50&height=50&seq=kpop-lb-001&orientation=squarish", xp: 52340, badge: "Legend", videos: 97 },
  { rank: 2, name: "DramaLover_Sora", avatar: "https://readdy.ai/api/search-image?query=young%20korean%20woman%20portrait%2C%20stylish%20drama%20fan%2C%20colorful%20background&width=50&height=50&seq=drama-lb-001&orientation=squarish", xp: 43210, badge: "Master", videos: 78 },
  { rank: 3, name: "FilmBuff_Jiwon", avatar: "https://readdy.ai/api/search-image?query=young%20korean%20man%20portrait%2C%20film%20enthusiast%20style%2C%20dark%20background&width=50&height=50&seq=film-lb-001&orientation=squarish", xp: 38760, badge: "Expert", videos: 65 },
  { rank: 4, name: "StarFan_Minjee", avatar: "https://readdy.ai/api/search-image?query=young%20korean%20woman%20portrait%2C%20kpop%20fan%20style%2C%20bright%20smile&width=50&height=50&seq=kpop-lb-004&orientation=squarish", xp: 31450, badge: "Pro", videos: 54 },
  { rank: 5, name: "WebtoonFan_Junho", avatar: "https://readdy.ai/api/search-image?query=young%20korean%20man%20portrait%2C%20webtoon%20fan%20style%2C%20colorful%20background&width=50&height=50&seq=webtoon-lb-001&orientation=squarish", xp: 24890, badge: "Creator", videos: 43 },
];

export const badges = [
  { id: "badge-001", name: "첫 영상 제작", icon: "ri-film-line", description: "첫 번째 영상 제작 완료", earned: true, color: "#ff6eb4" },
  { id: "badge-002", name: "드라마 마스터", icon: "ri-tv-2-line", description: "드라마 신 10개 제작", earned: true, color: "#3d9be9" },
  { id: "badge-003", name: "팬 스타", icon: "ri-star-line", description: "좋아요 1000개 달성", earned: true, color: "#ffd60a" },
  { id: "badge-004", name: "퀘스트 헌터", icon: "ri-sword-line", description: "퀘스트 5개 완료", earned: false, color: "#c77dff" },
  { id: "badge-005", name: "바이럴 크리에이터", icon: "ri-fire-line", description: "영상 공유 100회", earned: false, color: "#ff4757" },
  { id: "badge-006", name: "IP 레전드", icon: "ri-award-line", description: "모든 IP 카테고리 활용", earned: false, color: "#ff8c42" },
];
